import { useState, useEffect } from 'react';
import { Users, Play, Pause, Trash2, Eye, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { adminAPI } from '@/lib/api';

export default function SellerManagement() {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customersDialogOpen, setCustomersDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchSellers();
  }, []);

  const fetchSellers = async () => {
    try {
      const response = await adminAPI.getSellers();
      setSellers(response.data);
    } catch (error) {
      toast.error('Failed to fetch sellers');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (seller, newStatus) => {
    setProcessing(true);
    try {
      await adminAPI.updateSellerStatus(seller.id, newStatus);
      toast.success(`Seller ${newStatus}`);
      fetchSellers();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setProcessing(false);
    }
  };

  const handleViewCustomers = async (seller) => {
    setSelectedSeller(seller);
    try {
      const response = await adminAPI.getSellerCustomers(seller.id);
      setCustomers(response.data);
      setCustomersDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch customers');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setProcessing(true);
    try {
      await adminAPI.resetSellerPassword(selectedSeller.id, newPassword);
      toast.success('Password reset successfully. Email sent to seller.');
      setResetPasswordDialogOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setProcessing(false);
    }
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789@#$';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-secondary text-secondary-foreground">Active</Badge>;
      case 'paused':
        return <Badge variant="outline" className="bg-accent/10">Paused</Badge>;
      case 'deleted':
        return <Badge variant="destructive">Deleted</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="seller-management-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold">Seller Management</h1>
        <p className="text-muted-foreground mt-1">Manage all registered sellers on the platform</p>
      </div>

      {loading ? (
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-muted rounded" />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : sellers.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
              <Users className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="font-heading font-semibold mb-2">No sellers yet</h3>
            <p className="text-muted-foreground text-sm">Sellers will appear here when they sign up</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border/50">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Seller</TableHead>
                    <TableHead>Business</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>UPI ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sellers.map((seller) => (
                    <TableRow key={seller.id} data-testid={`seller-row-${seller.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{seller.name}</p>
                          <p className="text-sm text-muted-foreground">{seller.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{seller.business_name}</p>
                          <p className="text-sm text-muted-foreground">{seller.business_type}</p>
                        </div>
                      </TableCell>
                      <TableCell>{seller.contact_number}</TableCell>
                      <TableCell className="font-mono text-sm">{seller.upi_id}</TableCell>
                      <TableCell>{getStatusBadge(seller.status)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(seller.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewCustomers(seller)}
                            title="View Customers"
                            data-testid={`view-customers-${seller.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedSeller(seller);
                              setNewPassword('');
                              setResetPasswordDialogOpen(true);
                            }}
                            title="Reset Password"
                            data-testid={`reset-password-${seller.id}`}
                          >
                            <KeyRound className="h-4 w-4" />
                          </Button>
                          {seller.status === 'active' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusChange(seller, 'paused')}
                              disabled={processing}
                              title="Pause Seller"
                              data-testid={`pause-seller-${seller.id}`}
                            >
                              <Pause className="h-4 w-4" />
                            </Button>
                          ) : seller.status === 'paused' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-secondary"
                              onClick={() => handleStatusChange(seller, 'active')}
                              disabled={processing}
                              title="Activate Seller"
                              data-testid={`activate-seller-${seller.id}`}
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                          ) : null}
                          {seller.status !== 'deleted' && (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                setSelectedSeller(seller);
                                setDeleteDialogOpen(true);
                              }}
                              disabled={processing}
                              title="Delete Seller"
                              data-testid={`delete-seller-${seller.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Customers Dialog */}
      <Dialog open={customersDialogOpen} onOpenChange={setCustomersDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Customers - {selectedSeller?.business_name}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {customers.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No customers yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Orders</TableHead>
                    <TableHead>Total Spent</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.map((customer, index) => (
                    <TableRow key={index}>
                      <TableCell>{customer.name}</TableCell>
                      <TableCell>{customer.phone}</TableCell>
                      <TableCell>{customer.email || '-'}</TableCell>
                      <TableCell>{customer.orders_count}</TableCell>
                      <TableCell>₹{customer.total_spent.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Reset Seller Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-muted rounded-lg p-3">
              <p className="text-sm text-muted-foreground">Resetting password for:</p>
              <p className="font-medium">{selectedSeller?.name}</p>
              <p className="text-sm text-muted-foreground">{selectedSeller?.email}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <div className="flex gap-2">
                <Input
                  id="new-password"
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  data-testid="new-password-input"
                />
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={generateRandomPassword}
                  data-testid="generate-password-btn"
                >
                  Generate
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Minimum 6 characters. The seller will receive an email with the new password.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleResetPassword} 
              disabled={processing || !newPassword}
              data-testid="confirm-reset-password-btn"
            >
              {processing ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Seller</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedSeller?.business_name}"? This will soft-delete the seller account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                handleStatusChange(selectedSeller, 'deleted');
                setDeleteDialogOpen(false);
              }}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
